import { useEffect, useRef } from 'react';
import { useSelector } from 'src/redux/store';

const getVisualizerProps = (isMinimized: boolean) => {
  if (!isMinimized) {
    return {
      barWidth: 3,
      gap: 4,
      fillStyle: '#7a7a7a'
    };
  }

  return {
    barWidth: 1,
    gap: 1,
    fillStyle: 'white'
  };
};

export const useAudioVisualizer = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationRef = useRef<number | null>(null);
  const barBufferRef = useRef<number[] | null>(null);
  const ampEmaRef = useRef<number>(0);

  const { isMinimized, isPaused } = useSelector((state) => state.smartMeet);

  const isPausedRef = useRef(false);

  const {
    barWidth: initBarWidth,
    gap: initGap,
    fillStyle: initFillStyle
  } = getVisualizerProps(isMinimized);
  const barWidthRef = useRef(initBarWidth);
  const gapRef = useRef(initGap);
  const fillStyleRef = useRef(initFillStyle);

  useEffect(() => {
    const props = getVisualizerProps(isMinimized);
    barWidthRef.current = props.barWidth;
    gapRef.current = props.gap;
    fillStyleRef.current = props.fillStyle;
  }, [isMinimized]);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  const setupCanvas = (canvas: HTMLCanvasElement) => {
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();

    canvas.width = Math.max(1, Math.floor(rect.width * dpr));
    canvas.height = Math.max(1, Math.floor(rect.height * dpr));

    const ctx = canvas.getContext('2d')!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { ctx, w: rect.width, h: rect.height };
  };

  const drawWaveformBars = () => {
    if (!canvasRef.current || !analyserRef.current) return;

    const canvas = canvasRef.current;
    const { ctx, w, h } = setupCanvas(canvas);
    const analyser = analyserRef.current!;
    const timeData = new Uint8Array(analyser.fftSize);

    const barsCount = Math.max(
      20,
      Math.floor(w / (barWidthRef.current + gapRef.current))
    );
    const centerY = h / 2;
    const maxHeight = h * 4.8;
    const minDot = Math.max(2, h * 0.08);

    const ampEmaAlpha = 0.22;
    const newBarBoost = 1.0;

    if (!barBufferRef.current || barBufferRef.current.length !== barsCount) {
      barBufferRef.current = new Array(barsCount).fill(minDot);
    }

    const render = () => {
      animationRef.current = requestAnimationFrame(render);

      const buf = barBufferRef.current!;

      if (!isPausedRef.current) {
        analyser.getByteTimeDomainData(timeData);

        let sumSq = 0;
        for (let i = 0; i < timeData.length; i++) {
          const v = (timeData[i] - 128) / 128;
          sumSq += v * v;
        }
        const rms = Math.sqrt(sumSq / timeData.length);
        const targetAmp = Math.min(maxHeight, rms * 1.8 * maxHeight);

        const prev = ampEmaRef.current || 0;
        const smoothed = prev + (targetAmp - prev) * ampEmaAlpha;
        ampEmaRef.current = smoothed;

        for (let i = buf.length - 1; i > 0; i--) {
          buf[i] = buf[i - 1]; // no decay
        }

        buf[0] = Math.max(minDot, smoothed * newBarBoost);
      }

      const barWidth = barWidthRef.current;
      const gap = gapRef.current;
      const fillStyle = fillStyleRef.current;

      ctx.clearRect(0, 0, w, h);
      ctx.lineCap = 'round';
      ctx.strokeStyle = fillStyle;
      ctx.lineWidth = barWidth;

      for (let i = 0; i < buf.length; i++) {
        const height = buf[i];
        const x = i * (barWidth + gap) + barWidth / 2;
        const y1 = centerY - height / 2;
        const y2 = centerY + height / 2;

        if (height <= minDot + 0.5) {
          ctx.beginPath();
          ctx.arc(x, centerY, barWidth / 2, 0, Math.PI * 2);
          ctx.fillStyle = fillStyle;
          ctx.fill();
        } else {
          ctx.beginPath();
          ctx.moveTo(x, y1);
          ctx.lineTo(x, y2);
          ctx.stroke();
        }
      }
    };

    render();
  };

  const stopVisualizer = () => {
    if (animationRef.current != null) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch {}
      audioContextRef.current = null;
    }
    analyserRef.current = null;
  };

  const setupAudioGraph = (stream: MediaStream) => {
    audioContextRef.current = new (window.AudioContext ||
      // @ts-ignore
      (window as any).webkitAudioContext)();
    analyserRef.current = audioContextRef.current.createAnalyser();

    analyserRef.current.fftSize = 1024;
    analyserRef.current.minDecibels = -85;
    analyserRef.current.maxDecibels = -10;
    analyserRef.current.smoothingTimeConstant = 0.85;

    const source = audioContextRef.current.createMediaStreamSource(stream);
    source.connect(analyserRef.current);

    drawWaveformBars();
  };

  useEffect(() => {
    return () => stopVisualizer();
  }, []);

  return {
    canvasRef,
    setupAudioGraph,
    stopVisualizer
  };
};
